import React from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAsyncData } from "@/hooks/useAsyncData";
import { Money, StatusBadge, StoreDatePicker, SectionTitle, VerifiedTick, LoadErrorState, LoadingState, EmptyState } from "@/components/shared";
import FinalizePanel from "@/components/FinalizePanel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReceiptText, Wallet, Calculator, GitCompareArrows, FileText, ArrowRight, Printer } from "lucide-react";

const Stat = ({ label, paise, colored, testId, sub }) => (
  <Card className="rounded-lg">
    <CardContent className="p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg mt-0.5"><Money paise={paise} colored={colored} signed={colored} data-testid={testId} /></p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </CardContent>
  </Card>
);

const CashierDash = () => {
  const { user, today } = useApp();
  const q = useAsyncData(() => api.get("/reports/today").then((r) => r.data), []);
  if (q.error) return <LoadErrorState error={q.error} onRetry={q.reload} title="Could not load today's summary" />;
  if (q.loading || !q.data) return <LoadingState />;
  const data = q.data;
  const s = data.summary;
  const store = data.store;
  const count = data.count;
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-xl font-semibold arch-underline">Namaste, {user.name.split(" ")[0]}</h1>
          <p className="text-sm text-muted-foreground mt-1">Business date <span className="font-mono-num">{today}</span></p>
        </div>
        <div className="flex gap-2">
          <Button asChild className="bg-primary hover:bg-[hsl(var(--sapphire-2))]" data-testid="dash-new-bill-button">
            <Link to="/bills"><ReceiptText className="h-4 w-4 mr-1.5" />New Bill</Link>
          </Button>
        </div>
      </div>
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground -mb-3">Your collections today</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
        <Stat label="Your Bill Total" paise={s.bill_total_paise} testId="stat-bill-total" />
        <Stat label="Your Cash Received" paise={s.cash_from_bills_paise - s.cash_excess_returned_paise + s.adj_cash_receipts_paise} />
        <Stat label="Your Non-Cash" paise={s.noncash_from_bills_paise} />
        <Stat label="Less Taken" paise={s.less_taken_paise} />
        <Stat label="Your Expenses (cash)" paise={s.cash_expenses_paise} />
      </div>
      <Card className="rounded-lg">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Expected cash in store drawer (before HQ send)</p>
            <p className="text-2xl font-semibold glow-sapphire" data-testid="store-expected-cash"><Money paise={store.expected_pre_hq_paise} /></p>
            <p className="text-[11px] text-muted-foreground">
              Opening <Money paise={store.opening_paise} /> · cash on bills (all cashiers) <Money paise={store.cash_from_bills_paise} />
            </p>
            {store.opening_provisional && (
              <p className="text-[11px] text-[hsl(var(--warning))]" data-testid="cashier-provisional-note">
                Opening is provisional until {store.opening_source_date} is finalized
              </p>
            )}
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Store closing count</p>
            {count ? (
              <>
                <p className="text-2xl font-semibold"><Money paise={count.counted_paise} /></p>
                <p className="text-sm"><Money paise={count.variance_paise} colored signed />{" "}
                  <span className="text-[11px] text-muted-foreground">{count.variance_paise < 0 ? "shortage" : count.variance_paise > 0 ? "excess" : "balanced"} · by {count.submitted_by_name}</span>
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">Not submitted yet</p>
            )}
          </div>
          <div className="flex sm:justify-end">
            <Button asChild variant="outline" data-testid="dash-cash-count-button">
              <Link to="/cash-count"><Calculator className="h-4 w-4 mr-1.5" />{count ? "Update count" : "Submit closing count"}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {[
          { to: "/allocation", label: "Opening Cash", icon: Wallet },
          { to: "/adjustments", label: "Other Receipts", icon: FileText },
          { to: "/expenses", label: "Expenses", icon: FileText },
          { to: "/discrepancies", label: "Discrepancies", icon: GitCompareArrows },
        ].map((q) => (
          <Link key={q.to} to={q.to} className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border)/0.4)] bg-card card-zardozi p-3 text-sm font-medium hover:border-[hsl(var(--brass))] transition-colors duration-150" data-testid={`quick-${q.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <q.icon className="h-4 w-4 text-muted-foreground" />{q.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

const AccountantDash = () => {
  const { allowedStores, date } = useApp();
  const q = useAsyncData(
    async () => {
      if (!date || !allowedStores.length) return [];
      const rows = [];
      for (const s of allowedStores) {
        try {
          const [recon, exp] = await Promise.all([
            api.get("/recon/items", { params: { store_id: s.id, business_date: date } }),
            api.get("/expenses", { params: { store_id: s.id, business_date: date } }),
          ]);
          const expPending = exp.data.expenses.filter((e) => e.status === "active" && e.review_status === "unreviewed").length;
          rows.push({ store: s, pending: recon.data.status_counts?.pending || 0, unreviewed: recon.data.status_counts?.unreviewed || 0, total: recon.data.total_items, expPending });
        } catch {}
      }
      return rows;
    },
    [allowedStores, date]
  );
  const queues = q.data || [];
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-xl font-semibold arch-underline">Reconciliation work queue</h1>
        <StoreDatePicker hideStore />
      </div>
      {q.error && <LoadErrorState error={q.error} onRetry={q.reload} title="Could not load work queue" />}
      {q.loading && <LoadingState />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {queues.map((q) => (
          <Card key={q.store.id} className="rounded-lg" data-testid={`queue-card-${q.store.code}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-semibold">{q.store.name}</p>
                {q.pending > 0 && <StatusBadge status="pending" label={`${q.pending} Pending`} />}
              </div>
              <p className="text-sm text-muted-foreground">{q.total} non-cash entries · {q.unreviewed} unreviewed · {q.expPending} expenses to review</p>
              <div className="flex gap-2 pt-1">
                <Button asChild size="sm" variant="outline"><Link to="/reconciliation">Reconcile <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link></Button>
                <Button asChild size="sm" variant="ghost"><Link to="/expenses">Expenses</Link></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const ManagerAdminDash = () => {
  const { user, storeId, date } = useApp();
  const q = useAsyncData(
    () => {
      if (!storeId || !date) return Promise.resolve(null);
      return api.get("/reports/store-day", { params: { store_id: storeId, business_date: date } })
        .then((r) => r.data);
    },
    [storeId, date]
  );
  const report = q.data;
  const load = q.refresh;
  const a = report?.aggregate;
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-xl font-semibold arch-underline">{user.role === "admin" ? "Store overview" : "Store readiness"}</h1>
        <div className="flex items-center gap-2">
          <StoreDatePicker />
          <Button asChild variant="outline" size="sm" data-testid="day-close-report-button">
            <Link to={`/print/day-close?store_id=${storeId}&business_date=${date}`}>
              <Printer className="h-4 w-4 mr-1" />Day Close Report
            </Link>
          </Button>
          {user.role === "admin" && (
            <Button asChild variant="outline" size="sm" data-testid="go-comparison-button">
              <Link to="/comparison">Compare stores</Link>
            </Button>
          )}
        </div>
      </div>
      {q.error && <LoadErrorState error={q.error} onRetry={q.reload} title="Could not load the store-day report" />}
      {q.loading && <LoadingState />}
      {!q.loading && !q.error && !a && <EmptyState title="Select a store and date" sub="No report for the current selection" />}
      {a && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
            <Stat label="Opening" paise={a.opening_paise} sub={a.opening_source_date ? `from close of ${a.opening_source_date}${a.opening_provisional ? " (provisional)" : ""}` : "first day"} />
            <Stat label="Cash Receipts" paise={a.cash_receipts_paise + a.adj_cash_receipts_paise} />
            <Stat label="Non-Cash" paise={a.noncash_receipts_paise} />
            <Stat label="Cash Expenses" paise={-a.cash_expenses_paise} colored />
            <Stat label="Expected Cash (pre-HQ)" paise={a.expected_pre_hq_paise} />
            <Stat label="Variance" paise={a.variance_paise} colored sub={a.count_stale ? "count needs re-confirmation" : undefined} />
          </div>
          <div className="grid lg:grid-cols-[1fr_360px] gap-4 items-start">
            <Card className="rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 border-b flex items-center justify-between">
                <p className="font-display font-semibold text-sm">Cashier-wise collections (shared store drawer)</p>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <VerifiedTick finalized={a.status === "finalized"} />
                  <StatusBadge status={a.status === "finalized" ? "finalized" : "open-day"} />
                </span>
              </div>
              <div className="overflow-x-auto">
                <Table className="table-compact">
                  <TableHeader><TableRow>
                    <TableHead>Cashier</TableHead>
                    <TableHead className="text-right">Bills</TableHead>
                    <TableHead className="text-right">Bill Total</TableHead>
                    <TableHead className="text-right">Cash In</TableHead>
                    <TableHead className="text-right">Non-Cash</TableHead>
                    <TableHead className="text-right">Cash Expenses</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {report.cashiers.length === 0 && <TableRow><TableCell colSpan={6} className="text-xs text-muted-foreground text-center py-4">No activity yet</TableCell></TableRow>}
                    {report.cashiers.map((c) => (
                      <TableRow key={c.cashier_id} data-testid={`cashier-row-${c.cashier_name?.replace(/\s+/g, "-").toLowerCase()}`}>
                        <TableCell className="font-medium">{c.cashier_name}</TableCell>
                        <TableCell className="amount-cell">{c.bill_count}</TableCell>
                        <TableCell className="amount-cell"><Money paise={c.bill_total_paise} /></TableCell>
                        <TableCell className="amount-cell"><Money paise={c.cash_from_bills_paise - c.cash_excess_returned_paise + c.adj_cash_receipts_paise} /></TableCell>
                        <TableCell className="amount-cell"><Money paise={c.noncash_from_bills_paise} /></TableCell>
                        <TableCell className="amount-cell"><Money paise={c.cash_expenses_paise} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="px-4 py-2.5 border-t text-xs text-muted-foreground" data-testid="store-count-line">
                {report.count
                  ? <>Store closing count (pre-HQ) <Money paise={report.count.counted_paise} className="text-foreground font-semibold" /> vs expected <Money paise={report.count_state?.current_expected_pre_hq_paise ?? report.count.expected_paise} className="text-foreground" /> — variance <Money paise={report.count_state?.current_variance_paise ?? report.count.variance_paise} colored signed /> · by {report.count.submitted_by_name}
                      {report.count_state?.stale && <span className="block text-[hsl(var(--danger))] font-semibold mt-0.5" data-testid="count-stale-hint">Entries changed after this count — it must be confirmed or corrected on the Cash Count page before finalization.</span>}
                      {report.count_state?.next_opening_paise !== null && report.count_state?.next_opening_paise !== undefined && (
                        <span className="block mt-0.5">Cash sent to HQ from this close <Money paise={report.count_state.hq_sent_from_close_paise} className="text-foreground" /> · carries to next opening <Money paise={report.count_state.next_opening_paise} className="text-foreground font-semibold" /></span>
                      )}
                    </>
                  : <>Store closing count not submitted yet — expected (pre-HQ) <Money paise={a.expected_pre_hq_paise} className="text-foreground font-semibold" /></>}
              </div>
            </Card>
            <FinalizePanel storeId={storeId} businessDate={date} onChanged={load} />
          </div>
        </>
      )}
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useApp();
  if (!user) return null;
  if (user.role === "cashier") return <CashierDash />;
  if (user.role === "accountant") return <AccountantDash />;
  return <ManagerAdminDash />;
}
